/* eslint-disable no-unused-vars, indent */
/**
 * Implements a DEX Liquidity tab
 */
import { useState, useEffect } from 'react';
import ChartComp from '../ChartComponent';
import formatDistance from 'date-fns/formatDistance';
import Loader from '../../common/Loader';
import Text from '../../../atoms/Text';
import { KattanaAPI } from '../../../store/createStore';

const timeAbbreviate = (time: string) => {
  switch (time) {
    case 'months':
    case 'month':
      return 'mo';
    case 'days':
    case 'day':
      return 'd';
    case 'hours':
    case 'hour':
      return 'h';
    case 'minutes':
    case 'minute':
      return 'm';
    default:
      return time;
  }
};

const timeDistance = (time: Date) => {
  const [value, period] = formatDistance(new Date(), new Date(time))
    .replace('about ', '')
    .split(' ');

  return `${value}${timeAbbreviate(period)}`;
};

enum ELiqModes {
  ALL = '',
  ADDS = 'mint',
  REMOVES = 'burn',
}

interface ILiquidityItemProps {
  event: ILiquidityEvent;
  base_decimals: number;
  quote_decimals: number;
  base: string;
  quote: string;
  flipped: boolean;
}

/**
 * Single item in list
 */
const LiquidityItem = ({
  event,
  base_decimals,
  quote_decimals,
  base,
  quote,
  flipped,
}: ILiquidityItemProps) => (
  <div className={'liquidity__events__items__item text--weight-500'}>
    <Text
      size={12}
      className={`liquidity__events__columns__value text--${event.klass}  ${
        event.klass === 'mint' ? 'text--green-700' : 'text--red-700'
      }`}
    >
      ${event.value_usd.toFixed(2)}
    </Text>
    <div className='liquidity__events__columns__amount text--grey-400'>
      {flipped ? (
        <div>
          <Text size={12}>
            {Math.round((Number(event.amount1) / 10 ** base_decimals) * 100) /
              100}
            &nbsp;{base}
          </Text>

          <Text size={12}>
            {Math.round((Number(event.amount0) / 10 ** quote_decimals) * 100) /
              100}
            &nbsp;{quote}
          </Text>
        </div>
      ) : (
        <div>
          <Text size={12}>
            {Math.round((Number(event.amount0) / 10 ** base_decimals) * 100) /
              100}
            &nbsp;{base}
          </Text>
          <Text size={12}>
            {Math.round((Number(event.amount1) / 10 ** quote_decimals) * 100) /
              100}
            &nbsp;{quote}
          </Text>
        </div>
      )}
    </div>
    <Text size={12} className='liquidity__events__columns__age text--grey-400'>
      {timeDistance(event.date)}
    </Text>
  </div>
);

interface IDexCoinLiquidityProps {
  chain: string;
  address: string;
  base_decimals: number;
  quote_decimals: number;
  base: string;
  quote: string;
  flipped: boolean;
}

interface ILiquidityEvent {
  timestamp: number;
  date: Date;
  chain: string;
  blocknumber: number;
  pair: string;
  tx: string;
  protocol: string;
  klass: string;
  wallet: string;
  order: number;
  amount0: string;
  amount1: string;
  reserve0: string;
  reserve1: string;
  pricea: number;
  pricea_usd: number;
  priceb: number;
  priceb_usd: number;
  value_usd: number;
  reserve_usd: number;
}

interface ILiquidityData {
  reserve_usd: number;
  day: string;
}

/**
 * Remove some scammy strange values
 * @param values
 * @returns
 */
const decorateValues = (values: ILiquidityData[]) => {
  const result = values.reduce(
    (acc: ILiquidityData[], element: ILiquidityData, index: number) => {
      const normalized = Math.round(Number(element.reserve_usd) * 100) / 100;
      if (normalized <= 0) {
        if (index > 0) {
          acc.push({
            reserve_usd: acc[index - 1].reserve_usd,
            day: element.day,
          } as ILiquidityData);
        }
      } else {
        acc.push(element as ILiquidityData);
      }
      return acc;
    },
    []
  );

  return result;
};

const DexCoinLiquidity = (props: IDexCoinLiquidityProps) => {
  const [liqModes, setLiqModes] = useState(ELiqModes.ALL);
  const [liqLoading, setLiqLoading] = useState(true);
  const [liqError, setLiqError] = useState(false);
  const [liqData, setLiqData] = useState<ILiquidityData[]>([]);
  const [liqEvents, setEvents] = useState<ILiquidityEvent[]>([]);

  const liqCollection = liqEvents.filter((item: ILiquidityEvent) =>
    item.klass.includes(liqModes)
  );

  useEffect(() => {
    setLiqLoading(true);
    KattanaAPI
      .liquidity(props.chain, props.address, 'week')
      .then((res) => {
        setLiqError(false);
        setLiqData(decorateValues((res as any).daily));
        setEvents((res as any).events);
      })
      .catch(() => {
        setLiqError(true);
      })
      .finally(() => setLiqLoading(false));
  }, [props.chain, props.address]);

  if (liqLoading) {
    return <Loader />;
  }

  if ((!liqLoading && !liqData.length) || (!liqLoading && liqError)) {
    return (
      <Text size={24} className='text--grey-200 liquidity__lastValue'>
        No data
      </Text>
    ); // TODO: placeholder instead of this
  }

  if (!liqLoading && liqData.length > 0) {
    return (
      <div className='liquidity'>
        <Text size={24} className='text--grey-200 liquidity__lastValue'>
          $
          {Math.round(liqData[liqData.length - 1].reserve_usd).toLocaleString(
            'en-GB'
          )}
        </Text>
        <ChartComp data={liqData} />
        <div className='liquidity__events'>
          <div className='liquidity__events__modes text--weight-500'>
            <Text
              size={12}
              className={`liquidity__events__modes__item ${
                liqModes === ELiqModes.ALL
                  ? 'liquidity__events__modes__item--active'
                  : ''
              }`}
              onClick={() => setLiqModes(ELiqModes.ALL)}
            >
              All
            </Text>
            <Text
              size={12}
              className={`liquidity__events__modes__item ${
                liqModes === ELiqModes.ADDS
                  ? 'liquidity__events__modes__item--active'
                  : ''
              }`}
              onClick={() => setLiqModes(ELiqModes.ADDS)}
            >
              Adds
            </Text>
            <Text
              size={12}
              className={`liquidity__events__modes__item ${
                liqModes === ELiqModes.REMOVES
                  ? 'liquidity__events__modes__item--active'
                  : ''
              }`}
              onClick={() => setLiqModes(ELiqModes.REMOVES)}
            >
              Removes
            </Text>
          </div>
          <div className='liquidity__events__columns'>
            <Text
              size={10}
              className='liquidity__events__columns__value text--grey-600 text--weight-600'
            >
              Token value
            </Text>
            <Text
              size={10}
              className='liquidity__events__columns__amount text--grey-600 text--weight-600'
            >
              Token amount
            </Text>
            <Text
              size={10}
              className='liquidity__events__columns__age text--grey-600 text--weight-600'
            >
              Age
            </Text>
          </div>
          <div className='liquidity__events__items'>
            {liqCollection.map((event: ILiquidityEvent) => (
              <LiquidityItem
                key={`${event.tx}-${event.order}`}
                event={event}
                base_decimals={props.base_decimals}
                quote_decimals={props.quote_decimals}
                base={props.base}
                quote={props.quote}
                flipped={props.flipped}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
  return <div />;
};

export default DexCoinLiquidity;
